import React, { useCallback, useMemo } from 'react';
import { TextVariant, Text } from '@naf/text';
import { MobileCarousel } from '@naf-simba/sections-mobile-carousel';
import CardBenefitPartner from '@components/CardBenefitPartner/CardBenefitPartner';
import useSelector from '@redux/typedHooks';
import { ShowMoreButton } from '@components/ShowMoreButton/ShowMoreButton';
import { useActiveCampaigns } from '@hooks/useActiveCampaigns';
import { useGTMDataLayer } from '@hooks/GTM/useGTMDataLayer';
import * as S from './Styles';
import { ResultPage } from './ResultPage';

export const TimeLimitedResults = () => {
  const { items: pages } = useSelector((state) => state.membershipBenefitSearch);
  const {
    hasResults,
    showAll,
    allBenefitPages,
    limitedBenefitPages,
    displayedBenefits,
    benefitItems,
    toggleShowAll,
    isMobile,
  } = useActiveCampaigns(pages);
  const dataLayer = useGTMDataLayer();

  const benefitCards = useMemo(
    () =>
      benefitItems.map(({ id, itemKey, benefit }) => ({
        id,
        content: <CardBenefitPartner key={itemKey} benefit={benefit} disableFavorite />,
      })),
    [benefitItems],
  );

  const handleTrackClick = useCallback(
    (index: number) => {
      const benefit = benefitItems[index];
      if (!benefit || !dataLayer) return;

      dataLayer.push({
        event: 'limited_deal_benefits',
        interaction_type: 'click',
        benefit_id: benefitItems[index].benefit?.id || 'UNKNOWN',
        benefit_title: benefitItems[index].benefit?.title || 'UNKNOWN',
        benefit_position: index + 1,
        benefits_available: benefitItems.length,
      });
    },
    [benefitItems, dataLayer],
  );

  const handleTrackSwipe = useCallback(
    (index: number, prevIndex: number) => {
      const benefit = benefitItems[index];
      if (!benefit || !dataLayer) return;

      const swipe_direction = index > prevIndex ? 'right' : 'left';

      dataLayer.push({
        event: 'limited_deal_benefits',
        interaction_type: 'swipe',
        swipe_direction,
        swipe_from_position: prevIndex,
        swipe_to_position: index,
        benefits_available: benefitItems.length,
      });
    },
    [benefitItems, dataLayer],
  );

  if (!hasResults) {
    return null;
  }

  return (
    <>
      <S.HeaderWrapper>
        <S.Header>
          <S.Title $size="large">Tidsbegrensede kampanjer</S.Title>
          {isMobile ? null : (
            <S.Info>
              {allBenefitPages.length > 3 && <ShowMoreButton onClick={toggleShowAll} expanded={showAll} />}
              <Text tag="span" variant={TextVariant.Small}>
                {`Viser ${limitedBenefitPages.length} av ${allBenefitPages.length}`}
              </Text>
            </S.Info>
          )}
        </S.Header>
      </S.HeaderWrapper>
      {hasResults && isMobile ? (
        <S.SubGrid>
          <S.BenefitCarouselWrapper>
            <MobileCarousel
              items={benefitCards}
              cardHeight={367}
              onClickCallback={handleTrackClick}
              onSwipeCallback={handleTrackSwipe}
            />
          </S.BenefitCarouselWrapper>
        </S.SubGrid>
      ) : null}
      {hasResults && !isMobile
        ? Object.entries(displayedBenefits).map(([key, page]) => (
            <S.SubGrid key={key} $extraMarginBottom>
              <ResultPage page={page} disableFavorite />
            </S.SubGrid>
          ))
        : null}
    </>
  );
};
